import { useEffect } from 'react';
import {
  useController,
  useWatch,
  type Control,
  type FieldErrors,
  type FieldValues,
  type Path,
  type UseFormTrigger,
} from 'react-hook-form';
import { useIntl } from 'react-intl';
import PhoneInput, { type Country } from 'react-phone-number-input/input';
import FieldLabel from '@/shared/components/FieldLabel';
import FieldError from '@/shared/components/FieldError';
import { inputClass } from '@/shared/utils/styles';
import { hasFieldError, type ServerErrors } from '@/shared/utils/fieldErrors';
import { isValidPhone } from '../validation';

interface Props<T extends FieldValues> {
  control: Control<T>;
  trigger: UseFormTrigger<T>;
  errors: FieldErrors;
  serverErrors: ServerErrors;
}

export default function PhoneField<T extends { country: string; phone: string }>({
  control,
  trigger,
  errors,
  serverErrors,
}: Props<T>) {
  const intl = useIntl();
  const selectedCountry = useWatch({ control, name: 'country' as Path<T> }) as string | undefined;
  const country = selectedCountry ? (selectedCountry.toUpperCase() as Country) : undefined;
  const { field } = useController({
    control,
    name: 'phone' as Path<T>,
    rules: { validate: (value) => !value || isValidPhone(value as string, country) },
  });
  const { onChange, ...fieldProps } = field;

  useEffect(() => {
    void trigger('phone' as Path<T>);
  }, [country, trigger]);

  return (
    <div>
      <FieldLabel htmlFor="phone" labelId="auth.signUp.phoneLabel" optional />
      <PhoneInput
        {...fieldProps}
        id="phone"
        defaultCountry={country}
        autoComplete="tel"
        disabled={!country}
        className={inputClass(hasFieldError('phone', errors, serverErrors), !country)}
        placeholder={intl.formatMessage({
          id: country ? 'auth.signUp.phonePlaceholder' : 'auth.signUp.phoneAwaitingCountry',
        })}
        onChange={(value) => onChange(value ?? '')}
      />
      <FieldError field="phone" errors={errors} serverErrors={serverErrors}
        messages={{ validate: 'auth.signUp.phoneInvalid' }} />
    </div>
  );
}
